import type { CSSProperties, FC, MouseEvent, ReactElement } from 'react';
import { useMemo } from 'react';

import type {
  BadgeLabelSize,
  BadgeLabelTheme,
  BadgeLabelVariant,
  IconList,
} from '@snowit/icetypes';

import chroma from '@snowit/utils/chroma';
import testId from '@snowit/utils/testId';

import Icon from '../../Icon';
import Typo from '../../Typography/Typo';

import cx from 'classnames';
import s from './BadgeLabel.module.scss';

/**
 * @deprecated Use `IBadgeLabelV2`. See the `BadgeLabel` migration guide.
 */
export interface IBadgeLabel {
  /** The base styling to apply to the badge label */
  theme?: BadgeLabelTheme;
  variant?: BadgeLabelVariant;
  size?: BadgeLabelSize;
  /** The color of badge label background  */
  bgColor?: string;
  /** Add a classname to the badge label */
  className?: string;
  /** Add an icon to badge */
  icon?: IconList;
  /** @deprecated id of the component */
  id?: string;
  /** Text contained in the badge label */
  label: string;
  /** The color of badge label text  */
  labelColor?: string;
  letters?: number;
  /** @deprecated The name attribute of the badge  */
  name?: string;
  /** Function to be called on element label click */
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  /** Function to be called on remove icon click */
  onRemove?: (event: MouseEvent<HTMLDivElement>) => void;
  /** If true add text overflow and ellipsis */
  overflow?: boolean;
  /** Jest/Cypress test string */
  test?: string;
  /** if set will make the badge colored with a random seed color based on this id */
  colorID?: string;
}

/**
 * @deprecated Use `BadgeLabelV2`.
 *
 * Migration guide:
 * - `className` → `classNames.badge`
 * - `letters` → `sliceLabel`
 * - `theme="color"` → `theme="fill"`
 * - `theme="rounded"` → `theme="fill" shape="pill"`
 * - `theme="roundedOutline"` → `theme="outline" shape="pill"`
 * - `variant` → the corresponding `bgColor` and `labelColor`
 * - The default size changes from `df` to `xl`
 * - Explicit `size="df"` → `size="xxl"`; the badge will be larger
 * - `size="sm"` → `size="xl"`; the badge will be larger
 * - `size="xs"` → `size="df"`; the badge will be larger
 * - `theme="square"` and `theme="squareOutline"` have been removed
 * - `id` and `name` have been removed
 */
const BadgeLabel: FC<IBadgeLabel> = (props: IBadgeLabel): ReactElement => {
  const {
    size = 'df',
    icon,
    id,
    label,
    onClick,
    onRemove,
    overflow = true,
    letters = 1,
    className,
    test,
    colorID,
  } = props;

  const { bgColor, labelColor, theme } = useMemo(() => {
    if (colorID) {
      const [rndBG, rndLabel] = chroma.seedColor(colorID);
      return {
        bgColor: rndBG,
        labelColor: rndLabel,
        theme: 'color',
      };
    }
    switch (props.variant) {
      case 'gradient':
        return {
          bgColor: 'var(--cg-1)',
          labelColor: 'var(--c-white)',
          theme: 'color',
        };
      case 'primary':
        return {
          bgColor: `var(--c-${props.variant}-l2)`,
          labelColor: `var(--c-${props.variant})`,
          theme: 'color',
        };
      case 'error':
      case 'ok':
      case 'info':
      case 'warning':
        return {
          bgColor: `var(--c-${props.variant}-l)`,
          labelColor: `var(--c-${props.variant})`,
          theme: 'color',
        };
      case 'ok_fill':
      case 'error_fill':
      case 'info_fill':
      case 'warning_fill':
        return {
          bgColor: `var(--c-${props.variant.replace('_fill', '')})`,
          labelColor: 'var(--c-white)',
          theme: 'color',
        };
      default:
        return {
          bgColor: props.bgColor,
          labelColor: props.labelColor,
          theme: props.theme || 'outline',
        };
    }
  }, [props.variant, props.bgColor, props.labelColor, props.theme]);

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (typeof onClick !== 'function') return;
    onClick(event);
  };

  const handleRemove = (event: MouseEvent<HTMLDivElement>) => {
    if (typeof onRemove !== 'function') return;
    onRemove(event);
  };

  const renderIcon = () => {
    if (!icon) return null;
    if (theme === 'square' || theme === 'squareOutline') return null;
    return <Icon className={s.icon} name={icon} a11y={{ hidden: true }} />;
  };

  const renderLabel = () => {
    if (theme === 'square' || theme === 'squareOutline') {
      return <Typo className={s.label} text={label.slice(0, letters)} tag={'label'} size={'sm'} />;
    }
    return <Typo className={s.label} text={label} tag={'label'} size={'sm'} />;
  };

  const renderButton = () => {
    if (!onRemove) return null;
    if (theme === 'square' || theme === 'squareOutline') return null;
    return (
      <div className={s.removeBtn} onClick={handleRemove}>
        <Icon className={s.removeIcon} name={'close'} />
      </div>
    );
  };

  return (
    <div
      id={id}
      className={cx(s.badgeLabel, className, {
        [s[size]]: true,
        [s[theme]]: true,
        [s.overflow]: overflow,
        [s.onClick]: onClick,
      })}
      style={
        {
          '--bg-color': bgColor,
          '--label-color': labelColor,
        } as CSSProperties
      }
      onClick={handleClick}
      {...testId(test)}
    >
      {renderIcon()}
      {renderLabel()}
      {renderButton()}
    </div>
  );
};

export default BadgeLabel;
